import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  HardDrive,
  Server,
  Folder,
  FileText,
  ChevronRight,
  ChevronLeft,
  Home,
  Loader2,
  RefreshCw,
  ExternalLink,
  Network,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const FileViewer = ({ isOpen, onClose, file, connection }) => {
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && file) {
      loadFile();
    }
  }, [isOpen, file]);

  const loadFile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/external/read/${connection.id}`, {
        params: { path: file.path }
      });
      setContent(response.data.content);
    } catch (error) {
      toast.error("Failed to load file");
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/external/write/${connection.id}`, {
        connection_id: connection.id,
        path: file.path,
        content
      });
      toast.success("File saved");
    } catch (error) {
      toast.error("Failed to save file");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {file?.name}
            <Badge variant="secondary" className="ml-2">{connection?.protocol?.toUpperCase()}</Badge>
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <textarea
              className="flex-1 w-full p-4 font-mono text-sm bg-muted rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FileBrowser = ({ connection, onBack }) => {
  const [currentPath, setCurrentPath] = useState("/");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pathHistory, setPathHistory] = useState(["/"]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const fetchFiles = useCallback(async (path = "/") => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/external/list/${connection.id}`, {
        params: { path }
      });
      setFiles(response.data.items || []);
      setCurrentPath(path);
    } catch (error) {
      toast.error("Failed to load files");
    } finally {
      setIsLoading(false);
    }
  }, [connection.id]);

  useEffect(() => {
    fetchFiles("/");
  }, [fetchFiles]);

  const navigateTo = (path) => {
    const newHistory = [...pathHistory.slice(0, historyIndex + 1), path];
    setPathHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    fetchFiles(path);
  };

  const goBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      fetchFiles(pathHistory[newIndex]);
    }
  };

  const goForward = () => {
    if (historyIndex < pathHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      fetchFiles(pathHistory[newIndex]);
    }
  };

  const goHome = () => {
    navigateTo("/");
  };

  const handleItemClick = (item) => {
    if (item.type === "folder") {
      navigateTo(item.path);
    } else {
      setSelectedFile(item);
    }
  };

  const getProtocolIcon = () => {
    switch (connection.protocol) {
      case "smb":
        return <Network className="w-4 h-4" />;
      case "ftp":
      case "sftp":
        return <Server className="w-4 h-4" />;
      default:
        return <HardDrive className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Toolbar */}
      <div className="border-b border-border px-4 py-2 flex items-center gap-2 bg-muted/30">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8" 
          onClick={goBack}
          disabled={historyIndex === 0}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8" 
          onClick={goForward}
          disabled={historyIndex >= pathHistory.length - 1}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goHome}>
          <Home className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => fetchFiles(currentPath)}>
          <RefreshCw className="w-4 h-4" />
        </Button>
        
        <div className="flex-1 flex items-center gap-2 px-3 py-1 bg-background rounded border text-sm">
          {getProtocolIcon()}
          <span className="text-muted-foreground">{connection.name}:</span>
          <span className="truncate">{currentPath}</span>
        </div>
      </div>

      {/* File List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Folder className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">This folder is empty</p>
          </div>
        ) : (
          <div className="divide-y">
            {files.map((item, index) => (
              <div
                key={item.path}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleItemClick(item)}
                data-testid={`file-item-${index}`}
              >
                {item.type === "folder" ? (
                  <Folder className="w-5 h-5 text-primary/70" />
                ) : (
                  <FileText className="w-5 h-5 text-muted-foreground" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.type === "folder" ? "Folder" : `${formatSize(item.size)}`}
                    {item.modified && ` - ${item.modified}`}
                  </p>
                </div>
                {item.type !== "folder" && (
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* File Viewer */}
      <FileViewer
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        file={selectedFile}
        connection={connection}
      />
    </div>
  );
};

const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

export default function FileManagerPage() {
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/external/connections`);
      setConnections(response.data);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
      toast.error("Failed to load connections");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const getProtocolColor = (protocol) => {
    switch (protocol) {
      case "smb":
        return "bg-blue-500/10 text-blue-500 border-blue-500/30";
      case "ftp":
        return "bg-green-500/10 text-green-500 border-green-500/30";
      case "sftp":
        return "bg-purple-500/10 text-purple-500 border-purple-500/30";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/30";
    }
  };

  if (selectedConnection) {
    return (
      <div className="h-full flex flex-col" data-testid="file-browser">
        <FileBrowser 
          connection={selectedConnection} 
          onBack={() => setSelectedConnection(null)} 
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="file-manager-page">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HardDrive className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold">File Manager</h1>
          <span className="text-sm text-muted-foreground">({connections.length} connections)</span>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : connections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Server className="w-12 h-12 text-muted-foreground mb-4" />
            <h2 className="text-lg font-medium mb-2">No Connections</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Configure external connections in the Admin panel to browse remote files
            </p>
            <Button variant="outline" onClick={() => window.location.href = "/admin"}>
              Go to Admin Panel
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
            {connections.map((conn) => (
              <Card 
                key={conn.id} 
                className="hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => setSelectedConnection(conn)}
                data-testid={`connection-card-${conn.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {conn.protocol === "smb" ? (
                        <Network className="w-5 h-5 text-primary" />
                      ) : (
                        <Server className="w-5 h-5 text-primary" />
                      )}
                      <CardTitle className="text-base">{conn.name}</CardTitle>
                    </div>
                    <Badge className={cn("uppercase text-xs", getProtocolColor(conn.protocol))}>
                      {conn.protocol}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-1">
                    {conn.host}:{conn.port}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>User: {conn.username}</span>
                    <span>-</span>
                    <span>Path: {conn.base_path}</span>
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    <Folder className="w-4 h-4 mr-2" />
                    Browse Files
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
